import * as THREE from 'three';

import { getMaxPixelAxialDistance, createLabelMeasurementContext } from '../../../../gui/Label3D';
import LayerBackButton from "../LayerBackButton";
import { FacetTypes } from "../../../facets/Facets";
import { AnimatedWorldPos3D, getWorldBox } from '../../ui/positions';
import { LabelUI } from '../../ui/LabelUI';
import { CONTENT_ANCHORING } from '../../ui/PositionedUI';
import {
  CallbackOnEvent, DispatchEventOnHover, ExpandOnHover, HighlightDeferredOnHover, HighlightFacetOnHover, NestedMouseInteractionsPlugin,
  SelectDeferredOnClick, SelectFacetOnClick, UiCollision } from
'../../ui/plugins';
import { ClusteredCounts } from "../../ui/ClusterUI";

const LABEL_MAXW_FULL = 300;
const LABEL_MAXW_TRUNCATED = 100; // Same as max-width in SpacesLayer.css

const SPACE_LABEL_FONT = "400 11px ArtifaktElement";
const CLUSTER_REL_OFFSET = { top: '0', left: '50%', transform: 'translate(-12px, -24px)' };

let spaceMeasureCtx;

export class SpaceLabelUI extends LabelUI {
  #worldBox = new THREE.Box3();
  #estimatedFullSize;
  #estimatedTruncatedSize;
  #availablePx;
  #isFullLabel;

  constructor(hud, layer, pos3D, space) {
    super(hud, layer, new AnimatedWorldPos3D(space.model, space.dbId, pos3D), space.name, CONTENT_ANCHORING.CENTER);
    this.labelHost.classList.add('hud-room');

    this.data.node = space;
    this.data.node.id = space.spaceFacetId;
    this.data.model = space.model;
    this.data.dbId = space.dbId;
    this.data.facetType = FacetTypes.spaces;
    this.data.deferred = [];

    this.isSelected = false;
    this.isCollapsed = false;
    this.eventTarget = this.labelHost;
    this.collisionTarget = this.textHost;
    this.children = [];

    this.plugins.push(new UiCollision());
    this.plugins.push(new NestedMouseInteractionsPlugin());
    this.plugins.push(new ExpandOnHover());
    this.plugins.push(new CallbackOnEvent('dblclick', (e) => layer.onDblClick(e, space)));

    // Create a child object to represent only the text area of the label for eventing
    this.textBox = { eventTargets: [this.textHost], plugins: [] };
    this.textBox.plugins.push(new SelectFacetOnClick());
    this.textBox.plugins.push(new HighlightFacetOnHover());
    this.children.push(this.textBox);

    // Asset cluster integration
    const document = hud.getDocument();
    this.assetsCounts = new ClusteredCounts(document, CLUSTER_REL_OFFSET);
    this.assetsCounts.init(this.labelHost);
    this.assetsCounts.plugins.push(new SelectDeferredOnClick());
    this.assetsCounts.plugins.push(new HighlightDeferredOnHover());
    this.assetsCounts.plugins.push(new DispatchEventOnHover());
    this.children.push(this.assetsCounts);

    // TODO: refactor backBtn?
    this.backBtn = new LayerBackButton(hud.twin.facetsManager, hud.viewer.container.ownerDocument, FacetTypes.spaces, space.spaceFacetId);
    this.backBtn.attachToUI(this);

    if (!spaceMeasureCtx) {
      spaceMeasureCtx = createLabelMeasurementContext(hud.viewer);
      spaceMeasureCtx.font = SPACE_LABEL_FONT;
    }

    getWorldBox(this.data.model, this.data.dbId, this.#worldBox);
    this.#estimateLabelSize();
  }

  setSelected(value, faded) {
    if (value) {
      this.labelHost.classList.add("hud-room-selected");
      this.backBtn.show();
    } else {
      this.labelHost.classList.remove("hud-room-selected");
      this.backBtn.hide();
    }

    if (faded && !value) {
      this.labelHost.classList.add("hud-room-fade");
    } else {
      this.labelHost.classList.remove("hud-room-fade");
    }

    this.isSelected = value;

    if (this.isSelected) {
      this.uncollapse();
    }
  }

  updateRoomName() {
    const it = this.data.model.getInstanceTree();

    this.uncollapse();
    this.setText(it.getNodeName(this.data.dbId));
    this.#estimateLabelSize();
  }

  hasEnoughSpace() {
    const size = this.dimensions ?? this.getEstimatedSize();
    return this.#availablePx >= size.width;
  }

  updateWithDeferred(deferredAssets) {
    this.data.deferred.length = 0;
    for (const asset of deferredAssets) {
      this.data.deferred.push(asset);
    }

    this.assetsCounts.updateFromList(this.data.deferred);
  }

  getEstimatedSize() {
    if (this.isCollapsed) {
      return this.assetsCounts.getEstimatedSize();
    }

    return this.#isFullLabel ? this.#estimatedFullSize : this.#estimatedTruncatedSize;
  }

  collapse() {
    // Nothing to collapse into
    if (!this.assetsCounts.isVisible || this.isCollapsed || this.isSelected) return;

    this.isCollapsed = true;
    this.textHost.style.display = 'none';
    this.collisionTarget = this.labelHost;
    // Offset the label by 50% of cluster height to account for center anchoring
    // which is accounted for when computing collision bounds
    this.labelHost.style.transform = 'translateY(12px)';

    // Immediate inval so estimated size is used
    this.dimensions = null;
    this.updateDimensionsOnNextFrame();
  }

  uncollapse() {
    if (!this.isCollapsed) return;

    this.isCollapsed = false;
    this.textHost.style.display = 'flex';
    this.collisionTarget = this.textHost;
    this.labelHost.style.transform = 'translateY(0)';

    // Immediate inval so estimated size is used
    this.dimensions = null;
    this.updateDimensionsOnNextFrame();
  }

  #estimateLabelSize() {
    const measurements = spaceMeasureCtx.measureText(this.text);
    const textWidth = Math.abs(measurements.actualBoundingBoxRight) + Math.abs(measurements.actualBoundingBoxLeft);
    this.#estimatedFullSize = estimateCanvasTextDimensions(textWidth, LABEL_MAXW_FULL);
    this.#estimatedTruncatedSize = estimateCanvasTextDimensions(textWidth, LABEL_MAXW_TRUNCATED);
  }

  updateMaxWidth() {
    this.#availablePx = getMaxPixelAxialDistance(this.hud.viewer, this.#worldBox, true);
    const fullLabel = this.#availablePx * 2 > this.hud.viewer.canvasWrap.clientWidth;

    if (this.#isFullLabel === undefined || this.#isFullLabel !== fullLabel) {
      this.textHost.style.maxWidth = `${fullLabel ? LABEL_MAXW_FULL : LABEL_MAXW_TRUNCATED}px`;
      this.#isFullLabel = fullLabel;
      this.updateDimensionsOnNextFrame();
    }
  }
}

// The new implementation makes a fairly accurate estimation of the one-line dimensions,
// but a rather poor estimation of the wrapped footprint;
// this seems "good enough" for the compositor but can be improved.
// We can also update the estimated values with actual footprint when label becomes visible.
function estimateCanvasTextDimensions(textWidth, maxWidth) {
  const padding = 5; // per SpacesLayer.CSS
  const lineHeight = 18; // line height is set in SpacesLayer.css

  if (textWidth + padding > maxWidth) {
    return {
      // Wrapped
      width: maxWidth + padding,
      // Add an extra line to overestimate
      // This ensures label has enough space and avoids it turning off right
      // after actual dimensions are updated.
      height: (Math.ceil(textWidth / maxWidth) + 1) * lineHeight
    };
  }

  return {
    // Single line
    width: textWidth + padding,
    height: lineHeight
  };
}